import React, { useState, useEffect, useCallback } from 'react';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = ({ data, theme }) => {
  const [analytics, setAnalytics] = useState({
    roles: {},
    actions: {},
    methods: {},
    totalRecords: 0
  });
  
  const [modalState, setModalState] = useState({
    isOpen: false,
    content: null,
    title: ''
  });

  const calculateAnalytics = useCallback(() => {
    const roles = {};
    const actions = {};
    const methods = {};
    let totalRecords = 0;

    data.forEach(row => {
      if (row.length >= 4) {
        const role = row[0];
        const action = row[1];
        const method = row[2];

        // Подсчет ролей
        roles[role] = (roles[role] || 0) + 1;
        
        // Подсчет действий
        actions[action] = (actions[action] || 0) + 1;
        
        // Подсчет методов
        methods[method] = (methods[method] || 0) + 1;
        
        totalRecords++;
      }
    });

    setAnalytics({ roles, actions, methods, totalRecords });
  }, [data]);

  useEffect(() => {
    if (data && data.length > 0) {
      calculateAnalytics();
    }
  }, [data, calculateAnalytics]);

  const handleChartClick = (content, title) => {
    setModalState({
      isOpen: true,
      content,
      title
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      content: null,
      title: ''
    });
  };

  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Обработка клавиши Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && modalState.isOpen) {
        closeModal();
      }
    };

    if (modalState.isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Блокируем прокрутку страницы
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset'; // Восстанавливаем прокрутку
    };
  }, [modalState.isOpen]);

  const renderRoleChart = () => {
    const roleEntries = Object.entries(analytics.roles);
    const maxValue = Math.max(...roleEntries.map(([_, count]) => count));

    const chartContent = (
      <div className="bar-chart">
        {roleEntries.map(([role, count]) => (
          <div key={role} className="bar-item">
            <div className="bar-label">{role}</div>
            <div className="bar-wrapper">
              <div 
                className="bar-fill"
                style={{ 
                  width: `${(count / maxValue) * 100}%`,
                  backgroundColor: getRoleColor(role)
                }}
              >
                <span className="bar-value">{count}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="chart-container" onClick={() => handleChartClick(chartContent, "Распределение по ролям")}>
        <h3>Распределение по ролям</h3>
        {chartContent}
      </div>
    );
  };

  const renderActionChart = () => {
    const actionEntries = Object.entries(analytics.actions);
    
    const chartContent = (
      <div className="pie-chart-placeholder">
        {actionEntries.slice(0, 5).map(([action, count], index) => (
          <div key={action} className="pie-item">
            <div className="pie-color" style={{ backgroundColor: getActionColor(index) }}></div>
            <div className="pie-info">
              <span className="pie-label">{action}</span>
              <span className="pie-value">{count}</span>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="chart-container" onClick={() => handleChartClick(chartContent, "Топ действий")}>
        <h3>Топ действий</h3>
        {chartContent}
      </div>
    );
  };

  const renderMethodChart = () => {
    const methodEntries = Object.entries(analytics.methods);
    
    const chartContent = (
      <div className="method-grid">
        {methodEntries.map(([method, count]) => (
          <div key={method} className="method-card">
            <div className="method-icon">📋</div>
            <div className="method-name">{method}</div>
            <div className="method-count">{count}</div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="chart-container" onClick={() => handleChartClick(chartContent, "Методы работы")}>
        <h3>Методы работы</h3>
        {chartContent}
      </div>
    );
  };

  const renderDataTable = () => {
    return (
      <div className="table-container">
        <div className="table-header">
          <h3>Данные медицинских процессов</h3>
          <div className="table-stats">
            <span className="stat">Записей: {data.length}</span>
            <span className="stat">Колонок: {data[0]?.length || 0}</span>
          </div>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Роль</th>
                <th>Действие</th>
                <th>Способ</th>
                <th>Тип приема</th>
                <th>Колонка 5</th>
                <th>Колонка 6</th>
                <th>Колонка 7</th>
                <th>Колонка 8</th>
                <th>Колонка 9</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'even-row' : 'odd-row'}>
                  {row.map((cell, j) => (
                    <td key={j} className={`cell-${j}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getRoleColor = (role) => {
    const colors = {
      'Пациент': '#ff6b6b',
      'Врач': '#4ecdc4',
      'Мед.сестра': '#45b7d1',
      'Мед.регистратор': '#96ceb4'
    };
    return colors[role] || '#ddd';
  };

  const getActionColor = (index) => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'];
    return colors[index % colors.length];
  };

  if (!data || data.length === 0) {
    return (
      <div className="analytics-dashboard">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Загрузка данных для анализа...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`analytics-dashboard ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <div className="dashboard-header">
        <h2>Аналитика медицинских процессов</h2>
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number">{analytics.totalRecords}</span>
            <span className="stat-label">Всего записей</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{Object.keys(analytics.roles).length}</span>
            <span className="stat-label">Ролей</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{Object.keys(analytics.actions).length}</span>
            <span className="stat-label">Действий</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="chart-section">
          {renderRoleChart()}
        </div>
        
        <div className="chart-section">
          {renderActionChart()}
        </div>
        
        <div className="chart-section full-width">
          {renderMethodChart()}
        </div>
      </div>

      <div className="data-section">
        {renderDataTable()}
      </div>

      {/* Модальное окно для увеличенного просмотра графиков */}
      {modalState.isOpen && (
        <div className="chart-modal-overlay" onClick={handleModalBackdropClick}>
          <div className="chart-modal">
            <div className="chart-modal-header">
              <h3>{modalState.title}</h3>
              <button className="chart-modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="chart-modal-content">
              {modalState.content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard; 